import enMessages from '../../../../translations/en.json';
import {
  isExpiredRequestNotification,
  isHostPayoutNotification,
  isPaymentSuccessNotification,
  isPaymentWindowPassedNotification,
  isProviderNotificationTarget,
  isRequestPaymentNotification,
} from './notificationEventMeta';

const DEFAULT_LOCALE = 'en';
const SUPPORTED_LOCALES = [DEFAULT_LOCALE, 'th', 'fr', 'de', 'es'];
const HEADLINE_MESSAGE_ID_PATTERNS = [
  /^Notifications\.[a-zA-Z0-9-]+\.title$/,
  /^TransactionPage\.(default-booking|default-purchase|mobile-banking|promptpay)\.(customer|provider)\.[a-z-]+\.title$/,
  /^BookingStatus\.[a-zA-Z0-9-]+$/,
];
const SNIPPET_MESSAGE_ID_PATTERNS = [
  /^Notifications\.[a-zA-Z0-9-]+\.description$/,
  /^Notifications\.[a-zA-Z0-9-]+\.title$/,
  /^TransactionPage\.(default-booking|default-purchase|mobile-banking|promptpay)\.(customer|provider)\.[a-z-]+\.title$/,
  /^BookingStatus\.[a-zA-Z0-9-]+$/,
];

const PLACEHOLDER_REGEX = /\{\s*([a-zA-Z0-9_]+)\s*\}/g;

const buildCandidateMessageIds = patterns => {
  return Object.keys(enMessages).filter(id => patterns.some(pattern => pattern.test(id)));
};

const CANDIDATE_MESSAGE_IDS_BY_FIELD = {
  headline: buildCandidateMessageIds(HEADLINE_MESSAGE_ID_PATTERNS),
  snippet: buildCandidateMessageIds(SNIPPET_MESSAGE_ID_PATTERNS),
};

const compileCache = new Map();
const matchCache = new Map();

const escapeRegex = value => {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const compileTemplate = template => {
  if (compileCache.has(template)) {
    return compileCache.get(template);
  }

  const tokens = [];
  let cursor = 0;
  let pattern = '^';
  let match;

  while ((match = PLACEHOLDER_REGEX.exec(template)) !== null) {
    const [placeholder, token] = match;
    const start = match.index;

    pattern += escapeRegex(template.slice(cursor, start));
    pattern += `(?<${token}>.+?)`;
    tokens.push(token);
    cursor = start + placeholder.length;
  }

  pattern += escapeRegex(template.slice(cursor));
  pattern += '$';

  const compiled = {
    regex: new RegExp(pattern),
    tokens,
  };

  compileCache.set(template, compiled);
  return compiled;
};

const normalizeLocale = locale => {
  const value = String(locale || '').toLowerCase();
  const base = value.split('-')[0];

  if (SUPPORTED_LOCALES.includes(value)) {
    return value;
  }

  if (SUPPORTED_LOCALES.includes(base)) {
    return base;
  }

  return DEFAULT_LOCALE;
};

const getI18nFieldCandidates = field => {
  if (field === 'headline') {
    return ['title_i18n', 'titleI18n', 'headlineI18n'];
  }

  if (field === 'snippet') {
    return ['message_i18n', 'messageI18n', 'snippetI18n'];
  }

  return [];
};

const resolveNotificationMessageIdAndValues = ({ rawText, field }) => {
  const text = String(rawText || '');
  if (!text) {
    return null;
  }

  const cacheKey = `${field || 'headline'}:${text}`;
  if (matchCache.has(cacheKey)) {
    return matchCache.get(cacheKey);
  }

  const candidateMessageIds = CANDIDATE_MESSAGE_IDS_BY_FIELD[field] || CANDIDATE_MESSAGE_IDS_BY_FIELD.headline;

  for (const id of candidateMessageIds) {
    const template = enMessages[id];
    if (typeof template !== 'string' || !template.length) {
      continue;
    }

    const compiled = compileTemplate(template);
    const result = text.match(compiled.regex);

    if (result) {
      const values = compiled.tokens.reduce((collected, token) => {
        const value = result.groups?.[token];
        return {
          ...collected,
          [token]: value == null ? '' : String(value).trim(),
        };
      }, {});

      const resolved = { id, values };
      matchCache.set(cacheKey, resolved);
      return resolved;
    }
  }

  matchCache.set(cacheKey, null);
  return null;
};

const normalizeObjectKeys = value => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return Object.entries(value).reduce((res, [key, val]) => {
    return { ...res, [String(key).toLowerCase()]: val };
  }, {});
};

const parseMaybeJsonObject = value => {
  if (typeof value !== 'string') {
    return null;
  }

  const text = value.trim();
  if (!text.startsWith('{') || !text.endsWith('}')) {
    return null;
  }

  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

const pickLocalizedString = (value, locale) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    const parsed = parseMaybeJsonObject(value);
    if (parsed) {
      const loweredParsed = normalizeObjectKeys(parsed) || {};
      const normalizedLocale = normalizeLocale(locale);
      return (
        loweredParsed[normalizedLocale] ||
        loweredParsed.en ||
        Object.values(loweredParsed).find(item => typeof item === 'string' && item.trim()) ||
        null
      );
    }
    return value;
  }

  const normalizedObject = normalizeObjectKeys(value);
  if (!normalizedObject) {
    return null;
  }

  const normalizedLocale = normalizeLocale(locale);
  const lowered = normalizeObjectKeys(normalizedObject) || {};

  return (
    lowered[normalizedLocale] ||
    lowered.en ||
    Object.values(lowered).find(item => typeof item === 'string' && item.trim()) ||
    null
  );
};

const getPersistedLocalizedNotificationLine = ({ notification, field, locale }) => {
  const candidates = getI18nFieldCandidates(field);
  for (const key of candidates) {
    const localized = pickLocalizedString(notification?.[key], locale);
    if (typeof localized === 'string' && localized.trim()) {
      return localized;
    }
  }

  return null;
};

const getNotificationActorName = notification => {
  const value =
    notification?.actorName ||
    notification?.senderName ||
    notification?.providerName ||
    notification?.customerName ||
    '';

  const normalized = String(value).trim();
  return normalized || null;
};

const cleanupInterpolatedMessage = value => {
  return String(value || '')
    .replace(/\s+([,.!?])/g, '$1')
    .replace(/\b(with|to|by|from)\.$/i, '.')
    .replace(/\s+/g, ' ')
    .trim();
};

const formatNotificationMessage = ({ intl, id, defaultMessage, values }) => {
  return cleanupInterpolatedMessage(
    intl.formatMessage(
      {
        id,
        defaultMessage,
      },
      values
    )
  );
};

const getForcedNotificationLine = ({ notification, field, intl }) => {
  if (field !== 'headline' && field !== 'snippet') {
    return null;
  }

  const actorName = getNotificationActorName(notification);

  if (notification?.type === 'message.new' && field === 'headline' && actorName) {
    return formatNotificationMessage({
      intl,
      id: 'Notifications.newMessageFromSender.title',
      defaultMessage: 'You have a new message from {senderName}.',
      values: { senderName: actorName },
    });
  }

  if (isHostPayoutNotification(notification)) {
    if (field === 'headline') {
      return formatNotificationMessage({
        intl,
        id: 'Notifications.hostPayout.title',
        defaultMessage: 'You have been paid for your booking with {customerName}.',
        values: { customerName: actorName || '' },
      });
    }

    return intl.formatMessage({
      id: 'Notifications.hostPayout.description',
      defaultMessage: 'View your latest payout in Earnings.',
    });
  }

  if (isPaymentWindowPassedNotification(notification)) {
    return isProviderNotificationTarget(notification)
      ? formatNotificationMessage({
          intl,
          id: 'Notifications.paymentWindowPassedProvider.title',
          defaultMessage: "Booking cancelled. {customerName} didn't complete their payment.",
          values: { customerName: actorName || '' },
        })
      : intl.formatMessage({
          id: 'Notifications.paymentWindowPassedCustomer.title',
          defaultMessage: 'Booking cancelled. You missed the 48h payment deadline.',
        });
  }

  if (isExpiredRequestNotification(notification)) {
    return isProviderNotificationTarget(notification)
      ? formatNotificationMessage({
          intl,
          id: 'TransactionPage.default-booking.provider.expired.title',
          defaultMessage: 'The booking request from {customerName} has expired.',
          values: { customerName: actorName || '' },
        })
      : intl.formatMessage({
          id: 'TransactionPage.default-booking.customer.expired.title',
          defaultMessage: 'Your booking request expired.',
        });
  }

  const isCustomerPaymentReminder =
    !isProviderNotificationTarget(notification) &&
    (notification?.type === 'booking.paymentReminder' ||
      (notification?.type === 'booking.requested' && isRequestPaymentNotification(notification)));

  if (isCustomerPaymentReminder) {
    if (notification?.type === 'booking.requested') {
      return formatNotificationMessage({
        intl,
        id: 'Notifications.bookingRequestSent.title',
        defaultMessage: 'You sent a booking request to {providerName}.',
        values: { providerName: actorName || '' },
      });
    }

    return intl.formatMessage({
      id:
        field === 'headline'
          ? 'Notifications.paymentReminder.title'
          : 'Notifications.paymentReminder.description',
      defaultMessage:
        field === 'headline'
          ? 'Complete your payment now to confirm your booking.'
          : 'Action required: Payment pending',
    });
  }

  if (
    notification?.type === 'booking.updated' &&
    !isProviderNotificationTarget(notification) &&
    isPaymentSuccessNotification(notification)
  ) {
    return formatNotificationMessage({
      intl,
      id: 'Notifications.bookingPaymentReceivedCustomer.title',
      defaultMessage: "You're booked! Your payment was received and your booking is confirmed.",
    });
  }

  if (
    notification?.type === 'booking.updated' &&
    isProviderNotificationTarget(notification) &&
    isPaymentSuccessNotification(notification)
  ) {
    return intl.formatMessage({
      id: 'Notifications.bookingSecured.title',
      defaultMessage: "You're booked! Payment secured. Your payout arrives 72h after the shoot.",
    });
  }

  return null;
};

export const getLocalizedNotificationLine = ({ notification, field, intl }) => {
  const forcedNotificationLine = getForcedNotificationLine({
    notification,
    field,
    intl,
  });
  if (forcedNotificationLine) {
    return forcedNotificationLine;
  }

  const persistedLocalizedText = getPersistedLocalizedNotificationLine({
    notification,
    field,
    locale: intl?.locale,
  });
  if (persistedLocalizedText) {
    return persistedLocalizedText;
  }

  const rawText = notification?.[field];
  if (!rawText || typeof rawText !== 'string') {
    return rawText || '';
  }

  if (field === 'headline' && rawText === 'New Message') {
    return intl.formatMessage({
      id: 'Notifications.newMessage',
      defaultMessage: 'New Message',
    });
  }

  if (field === 'snippet' && rawText === 'You have a new message.') {
    return intl.formatMessage({
      id: 'Notifications.newMessageFallbackSnippet',
      defaultMessage: 'You have a new message.',
    });
  }

  const resolved = resolveNotificationMessageIdAndValues({
    rawText,
    field,
  });
  if (!resolved) {
    return rawText;
  }

  return intl.formatMessage(
    {
      id: resolved.id,
      defaultMessage: enMessages[resolved.id] || rawText,
    },
    resolved.values
  );
};

export const getLocalizedListingTitle = ({ notification, locale }) => {
  const normalizedLocale = normalizeLocale(locale);
  const listing = notification?.listing;

  const localizedFromListingObject =
    listing?.attributes?.publicData?.thLanguage?.[normalizedLocale]?.title ||
    pickLocalizedString(listing?.attributes?.title, normalizedLocale) ||
    pickLocalizedString(listing?.title, normalizedLocale);

  const localizedFromNotification =
    pickLocalizedString(notification?.listingNameI18n, normalizedLocale) ||
    pickLocalizedString(notification?.listingTitleI18n, normalizedLocale) ||
    pickLocalizedString(notification?.listingNameByLocale, normalizedLocale) ||
    pickLocalizedString(notification?.listingTitleByLocale, normalizedLocale);

  return (
    localizedFromListingObject ||
    localizedFromNotification ||
    notification?.listingName ||
    notification?.listingTitle ||
    ''
  );
};
